import { customElement, property, state } from 'lit/decorators.js'
import { CSSResultGroup, html, LitElement, nothing, PropertyValues } from 'lit'
import { styles } from 'components/shared/account/header.style.js'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'
import { classMap } from 'lit/directives/class-map.js'
import { CSEBadge } from '@cloudsuite/mosaic-elements'

@customElement('csc-account-header')
export class CSCAccountHeader extends LitElement {
  static override styles: CSSResultGroup = [styles]

  @property({ type: Boolean, reflect: true, attribute: 'hide-divider' })
  hideDivider: boolean = false

  @property({ type: String, reflect: true, attribute: 'heading-icon-role' })
  headingIconRole: string = ''

  @state()
  slottedBadgeElementExists: CSEBadge | null = null

  private _badgeChecked = false

  protected override updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties)

    this.checkSlottedBadgeIsCSEBadge()
  }

  private checkSlottedBadgeIsCSEBadge() {
    if (!this._badgeChecked) {
      setTimeout(() => { // Hack..
        const slottedBadge = this.querySelector('[slot="badge"]')
        const badgeChildNodeList = Array.from(slottedBadge?.shadowRoot?.childNodes || [])
        badgeChildNodeList.map(
          node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as HTMLElement
              // if (element.tagName.toLowerCase() === CSEBadge.name) {
              if (element.tagName.toLowerCase() === 'cse-badge') {
                this.slottedBadgeElementExists = element as CSEBadge
              }
            }
          }
        )
      }, 0)
    }
  }

  private get hasMetaData(): boolean {
    return !this.isSlotEmpty('badge') || !this.isSlotEmpty('date') || !this.isSlotEmpty('description')
  }

  private isSlotEmpty(slotName: string): boolean {
    const slotElement = this.querySelector(`[slot="${slotName}"]`)
    return !slotElement
  }

  private slotAttributes(slotName: string): Record<string, string> {
    const slotElement = this.querySelector(`[slot="${slotName}"]`)
    if (!slotElement) return {}

    const attributes: Record<string, string> = {}
    for (const attr of slotElement.attributes) {
      if (attr.name === 'slot') continue
      if (attr.name.startsWith('data-')) {
        attributes[attr.name.replace('data-', '')] = attr.value
      } else {
        attributes[attr.name] = attr.value
      }
    }
    return attributes
  }

  private renderHeadingIcon() {
    if (this.headingIconRole.trim() === '') {
      return nothing
    }

    return html`
      <cse-icon
        class="heading-icon"
        icon-role="${this.headingIconRole}"
        size="xl"
      ></cse-icon>
    `
  }

  private renderHeading() {
    if (this.isSlotEmpty('heading')) {
      return nothing
    }


    const attributes = this.slotAttributes('heading')
    const htmlString = `<cse-text class="heading-text" as="h1" variant="heading-1" ${Object.entries(attributes)
      .map(([key, value]) => `${key}="${value}"`)
      .join(' ')}>
      <slot name="heading"></slot>
    </cse-text>`
    return html`${unsafeHTML(htmlString)}`
  }

  private renderBadge() {
    if (this.isSlotEmpty('badge')) {
      return nothing
    }

    if (this.slottedBadgeElementExists) {
      return this.slottedBadgeElementExists
    }

    const attributes = this.slotAttributes('badge')
    const htmlString: string = `<cse-badge class="badge" ${Object.entries(attributes)
      .map(([key, value]) => `${key}="${value}"`)
      .join(' ')}>
      <slot name="badge"></slot>
    </cse-badge>`
    return html`${unsafeHTML(htmlString)}`
  }

  private renderDate() {
    if (this.isSlotEmpty('date')) {
      return nothing
    }

    const attributes = this.slotAttributes('date')
    const htmlString = `<cse-text class="date" as="p" variant="body-s" ${Object.entries(attributes)
      .map(([key, value]) => `${key}="${value}"`)
      .join(' ')}>
      <slot name="date"></slot>
    </cse-text>`
    return html`${unsafeHTML(htmlString)}`
  }

  private renderDescription() {
    if (this.isSlotEmpty('description')) {
      return nothing
    }

    throw new Error('csc-account-header: slot "description" is not supported yet.')
  }

  private renderTopChips() {
    if (this.isSlotEmpty('top-chips')) {
      return nothing
    }

    const attributes = this.slotAttributes('top-chips')
    const htmlString = `<cse-chip-group class="tip-chops" ${Object.entries(attributes)
      .map(([key, value]) => `${key}="${value}"`)
      .join(' ')}>
      <slot name="top-chips"></slot>
    </cse-chip-group>`
    return html`${unsafeHTML(htmlString)}`
  }

  private renderTopAction() {
    if (this.isSlotEmpty('top-action')) {
      return nothing
    }

    return html`<slot name="top-action"></slot>`
  }

  private renderBottomInputField() {
    if (this.isSlotEmpty('bottom-input-field')) {
      return nothing
    }

    throw new Error('csc-account-header: slot "bottom-input-field" is not supported yet.')
  }

  private renderBottomButtons() {
    if (this.isSlotEmpty('bottom-buttons')) {
      return nothing
    }

    return html`<div class="bottom-buttons">
      <slot name="bottom-buttons"></slot>
    </div>`
  }

  override render() {
    return html`
      <header>
        <div class="${classMap({ top: true, 'no-meta-data': !this.hasMetaData })}">
          <div class="${classMap({
            heading: true,
            'heading-icon': this.headingIconRole.trim() !== '',
          })}">
            ${this.renderHeadingIcon()}
            ${this.renderHeading()}
          </div>

          ${this.hasMetaData ? html`<div class="meta-data">
            ${this.renderBadge()}
            ${this.renderDate()}
            ${this.renderDescription()}
          </div>` : nothing}

          <div class="chips">
            ${this.renderTopChips()}
            ${this.renderTopAction()}
          </div>
        </div>

        ${this.hideDivider ? nothing : html`<cse-divider class="divider" orientation="horizontal" variant="solid"></cse-divider>`}

        <div class="bottom">
          ${this.renderBottomInputField()}
          ${this.renderBottomButtons()}
        </div>
      </header>
    `
  }
}
